import AudiencesController from '@mm/state/controllers/AudiencesController';
import { Variable } from '@mm/state/interfaces/audiences';
import {
  FilterItem,
  FilterItemOperator,
  VariableType,
} from '@mm/state/model/AudiencesModel';
import { VariableOptionsModel } from '@mm/state/model/VariableOptionsModel';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import './AudienceFilterItemInput.pcss';
import { AudienceFilterItemInputOperator } from './AudienceFilterItemInputOperator';
import { AudienceFilterItemInputValue } from './AudienceFilterItemInputValue';
import { AudienceFilterItemInputVariable } from './AudienceFilterItemInputVariable';

interface OuterProps {
  filter: FilterItem;
  setFilter: (filter: FilterItem) => void;
  className?: string;
  hideOperatorAndValueUntilVariableSelected?: boolean;
  showBlankVariableOption?: boolean;
  setInputRef?: (ref: HTMLInputElement) => void;
  withEmoji?: boolean;
  css?: {
    section?: string;
    fieldTitle?: string;
    select?: string;
    selectActiveItem?: string;
    suggestionsContainer?: string;
    valueInput?: string;
    valueButton?: string;
    valueListing?: string;
    valueTagItem?: string;
  };
  chipTagItems?: boolean;
  withIcons?: boolean;
}
type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;
type Props = OuterProps & InnerProps;

export const MULTI_VALUES_OPERATORS: FilterItemOperator[] = [
  'equal',
  'not_equal',
];

export interface AudienceFilterItemInputChildProps {
  variables: Variable[];

  filter: FilterItem;
  setFilter: (filter: FilterItem) => void;
  variableOptions: VariableOptionsModel | undefined;
  variableOperators: Partial<VariableOptionsModel> | undefined;
  allowMultipleValues: boolean;
  busy: boolean;
  className?: string;
  showBlankVariableOption?: boolean;
  setInputRef?: (ref: HTMLInputElement) => void;
  fetchingVariableOptions: boolean;
  fetchingVariableOperators?: boolean;
  withEmoji?: boolean;
  css?: OuterProps['css'];
  chipTagItems?: boolean;
  withIcons?: boolean;
}

const AudienceFilterItemInputComponent = (props: Props) => {
  const {
    variables,
    getVariableOptions,
    chatbotId,
    filter,
    setFilter,
    className,
    hideOperatorAndValueUntilVariableSelected,
    showBlankVariableOption,
    setInputRef,
    withEmoji,
    css,
    chipTagItems,
    withIcons,
  } = props;

  const localSetFilter = useCallback(
    (filter: FilterItem) => {
      setFilter({
        ...filter,
        valid: !!(filter.operator && filter.value.length),
      });
    },
    [setFilter],
  );

  const [variableOptions, setVariableOptions] = useState<VariableOptionsModel>({
    variableDataKind: 'string',
    variableOperators: [],
    variableOptions: [],
  });
  const [variableOperators, setVariableOperators] = useState<
    Partial<VariableOptionsModel>
  >({
    variableDataKind: 'string',
    variableOperators: [],
  });
  const [fetchingVariableOptions, setFetchingVariableOptions] = useState(false);
  const [fetchingVariableOperators, setFetchingVariableOperators] =
    useState(false);

  useEffect(() => {
    if (!filter.variableId) return;

    const updateVariableOptions = async () => {
      setFetchingVariableOperators(true);
      setFetchingVariableOptions(true);
      try {
        let filterUpdates = {};

        if (!fetchingVariableOperators) {
          const operators = await getVariableOptions(
            chatbotId,
            filter.variableId,
            filter.variableType,
            filter.variable,
            false,
          );
          setVariableOperators(operators);
          setFetchingVariableOperators(false);

          if (!filter.operator) {
            const operator = operators.variableOperators?.[0]?.value;

            filterUpdates = {
              ...filterUpdates,
              operator,
              value: MULTI_VALUES_OPERATORS.includes(operator) ? [] : '',
            };
          }
        }

        const datesFormat = filter.esType ? 'YYYY-MM-DD' : 'MM/DD/YYYY';

        if (!fetchingVariableOptions) {
          const options = await getVariableOptions(
            chatbotId,
            filter.variableId,
            filter.variableType,
            filter.variable,
            true,
          );
          setVariableOptions(options);
          if (
            options.variableDataKind === 'date_time' &&
            !moment(filter.value, datesFormat).isValid()
          ) {
            filterUpdates = {
              ...filterUpdates,
              value: moment().format(datesFormat),
            };
          }
        }

        if (Object.keys(filterUpdates).length) {
          localSetFilter({ ...filter, ...filterUpdates });
        }
      } finally {
        setFetchingVariableOptions(false);
      }
    };

    updateVariableOptions();
  }, [filter.variableId, filter.variableType]);

  const allowMultipleValues = !!(
    variableOptions && MULTI_VALUES_OPERATORS.find(o => o === filter.operator)
  );

  const childProps: AudienceFilterItemInputChildProps = {
    filter,
    setFilter: localSetFilter,
    variables,
    variableOptions,
    variableOperators,
    allowMultipleValues,
    busy: fetchingVariableOptions,
    className,
    showBlankVariableOption,
    setInputRef,
    fetchingVariableOptions,
    fetchingVariableOperators,
    withEmoji,
    css,
    chipTagItems,
    withIcons,
  };

  return (
    <div className={classNames('audience-filter-item-input', className)}>
      <AudienceFilterItemInputVariable {...childProps} />
      {(!hideOperatorAndValueUntilVariableSelected || !!filter.variableId) && (
        <AudienceFilterItemInputOperator {...childProps} />
      )}
      {(!hideOperatorAndValueUntilVariableSelected || !!filter.variableId) && (
        <AudienceFilterItemInputValue {...childProps} />
      )}
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  chatbotId: state.view.chatbotEditor.activeChatbotId,
  variables: state.audiences.variables,
});
const mapDispatchToProps = () => ({
  getVariableOptions: (
    chatbotId: number,
    variableId: number,
    variableType: VariableType,
    variableName: string,
    withOptions?: boolean,
  ) =>
    AudiencesController.getVariableOptions(
      chatbotId,
      variableId,
      variableType,
      variableName,
      withOptions,
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export const AudienceFilterItemInput = enhance(
  AudienceFilterItemInputComponent,
);
